import React, { useEffect, useRef, useState } from 'react';
import { Shield, AlertCircle, CheckCircle2 } from 'lucide-react';
import { env } from '../utils/env';

interface ReCaptchaProps {
  onVerify: (token: string | null) => void;
  siteKey?: string;
  theme?: 'light' | 'dark';
  size?: 'normal' | 'compact';
  className?: string;
}

declare global {
  interface Window {
    grecaptcha?: any;
    onRecaptchaLoad?: () => void;
  }
}

const ReCaptcha: React.FC<ReCaptchaProps> = ({
  onVerify,
  siteKey = env.recaptchaSiteKey, // ✅ Utilisation de l'utilitaire env
  theme = 'light',
  size = 'normal',
  className = ''
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string>('');
  const [widgetId, setWidgetId] = useState<number | null>(null);
  const recaptchaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Debug info
    env.log('ReCaptcha initializing with key:', env.isTestRecaptcha() ? 'Test Key' : 'Production Key');
    
    // Vérifier si reCAPTCHA est déjà chargé
    if (window.grecaptcha && window.grecaptcha.render) {
      setIsLoaded(true);
      renderRecaptcha();
      return;
    }

    // Charger le script reCAPTCHA
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit`;
    script.async = true;
    script.defer = true;

    // Callback global pour le chargement
    window.onRecaptchaLoad = () => {
      env.log('ReCaptcha script loaded successfully');
      setIsLoaded(true);
      renderRecaptcha();
    };

    document.head.appendChild(script);

    return () => {
      // Nettoyage
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      if (window.onRecaptchaLoad) {
        window.onRecaptchaLoad = undefined;
      }
    };
  }, []);

  const renderRecaptcha = () => {
    if (!window.grecaptcha || !recaptchaRef.current || widgetId !== null) {
      return;
    }

    try {
      const id = window.grecaptcha.render(recaptchaRef.current, {
        sitekey: siteKey,
        theme: theme,
        size: size,
        callback: handleVerify,
        'expired-callback': handleExpired,
        'error-callback': handleError
      });
      setWidgetId(id);
      setError('');
      env.log('ReCaptcha widget rendered with ID:', id);
    } catch (err) {
      env.error('Erreur lors du rendu reCAPTCHA:', err);
      setError('Erreur lors du chargement du captcha');
    }
  };

  const handleVerify = (token: string) => {
    env.log('ReCaptcha verified successfully');
    setIsVerified(true);
    setError('');
    onVerify(token);
  };

  const handleExpired = () => {
    env.warn('ReCaptcha token expired');
    setIsVerified(false);
    setError('Le captcha a expiré, veuillez le refaire');
    onVerify(null);
  };

  const handleError = () => {
    env.error('ReCaptcha verification error');
    setIsVerified(false);
    setError('Erreur lors de la vérification du captcha');
    onVerify(null);
  };

  const reset = () => {
    if (window.grecaptcha && widgetId !== null) {
      window.grecaptcha.reset(widgetId);
      setIsVerified(false);
      setError('');
      onVerify(null);
      env.log('ReCaptcha reset');
    }
  };

  // Version simplifiée pour le développement
  const handleSimpleVerification = () => {
    if (env.isTestRecaptcha()) {
      env.log('Using test reCaptcha verification');
      setIsVerified(true);
      onVerify('dev-token-' + Date.now());
    }
  };

  // Si clé de test, afficher une version simplifiée
  if (env.isTestRecaptcha()) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center space-x-3">
          <div
            className={`
              flex items-center justify-center w-12 h-12 border-2 rounded cursor-pointer transition-colors
              ${isVerified 
                ? 'border-green-500 bg-green-50' 
                : 'border-gray-300 hover:border-primary'
              }
            `}
            onClick={handleSimpleVerification}
          >
            {isVerified ? (
              <CheckCircle2 className="w-6 h-6 text-green-500" />
            ) : (
              <Shield className="w-6 h-6 text-gray-400" />
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">
              {isVerified ? 'Vérification réussie' : 'Je ne suis pas un robot'}
            </p>
            <p className="text-xs text-gray-500">
              {env.environment === 'development' ? 'Mode développement' : 'Captcha de test'}
            </p>
          </div>
        </div>
        
        {/* {env.isDebug && (
          <div className="text-xs text-orange-600 bg-orange-50 p-2 rounded">
            ⚠️ Mode test reCAPTCHA - Configurez VITE_RECAPTCHA_SITE_KEY pour la production
          </div>
        )} */}
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Container reCAPTCHA */}
      <div className="flex items-start space-x-3">
        {!isLoaded ? (
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 border-2 border-gray-300 rounded animate-pulse bg-gray-100"></div>
            <div>
              <p className="text-sm text-gray-600">Chargement du captcha...</p>
            </div>
          </div>
        ) : (
          <div className="flex items-start space-x-3">
            <div ref={recaptchaRef}></div>
            {isVerified && (
              <div className="flex items-center space-x-2 mt-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-sm text-green-600">Vérifié</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{error}</p>
            <button
              type="button"
              onClick={reset}
              className="text-xs text-red-600 hover:text-red-800 underline mt-1"
            >
              Réessayer
            </button>
          </div>
        </div>
      )}

      {/* Informations de confidentialité */}
      <div className="text-xs text-gray-500">
        Ce site est protégé par reCAPTCHA et les{' '}
        <a 
          href="https://policies.google.com/privacy" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          Règles de confidentialité
        </a>{' '}
        et{' '}
        <a 
          href="https://policies.google.com/terms" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          Conditions d'utilisation
        </a>{' '}
        de Google s'appliquent.
      </div>
    </div>
  );
};

export default ReCaptcha;